import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { userPerms } from "@/lib/permissions";
import {
  CarIcon,
  CheckCircleIcon,
  HourglassMediumIcon,
  CurrencyEurIcon,
  StarIcon,
  ArrowRightIcon,
} from "@phosphor-icons/react";

const fmtEUR = (n) =>
  new Intl.NumberFormat("el-GR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

function StatCell({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="p-6 border border-black/10 bg-white flex flex-col justify-between min-h-[140px]">
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">{label}</div>
        <Icon size={22} weight="regular" color={accent || "#0A0A0A"} />
      </div>
      <div>
        <div className="heading text-4xl font-extrabold tracking-tight">{value}</div>
        {sub && <div className="text-xs text-[#4B5563] mt-1 font-mono">{sub}</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const canAnalytics = userPerms(user).includes("analytics");
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, list, an] = await Promise.all([
        api.get("/cars/stats"),
        api.get("/cars"),
        canAnalytics ? api.get("/cars/analytics").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      ]);
      setStats(s.data);
      setRecent(list.data.slice(0, 8));
      setAnalytics(an.data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-black/10 -mt-px">
        <div className="border-b border-r border-black/10">
          <StatCell
            label="Σύνολο Αυτοκινήτων"
            value={stats?.total ?? "·"}
            sub="στο σύστημα"
            icon={CarIcon}
          />
        </div>
        <div className="border-b border-r border-black/10">
          <StatCell
            label="Διαθέσιμα"
            value={stats?.available ?? "·"}
            sub="προς πώληση"
            icon={CheckCircleIcon}
            accent="#16A34A"
          />
        </div>
        <div className="border-b border-r border-black/10">
          <StatCell
            label="Κρατημένα"
            value={stats?.reserved ?? "·"}
            sub="σε διαπραγμάτευση"
            icon={HourglassMediumIcon}
            accent="#F59E0B"
          />
        </div>
        <div className="border-b border-r border-black/10">
          <StatCell
            label="Αξία Στόλου"
            value={fmtEUR(stats?.total_inventory_value)}
            sub={`${stats?.featured ?? 0} προβεβλημένα`}
            icon={CurrencyEurIcon}
            accent="#0055FF"
          />
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-black/10">
        <Link
          to="/cars/new"
          data-testid="quick-add-car"
          className="border-b border-r border-black/10 p-6 hover:bg-[#F9FAFB] transition-colors group flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">Καταχώρηση</div>
            <div className="heading text-xl font-bold mt-1">Νέο Αυτοκίνητο</div>
            <div className="text-xs text-[#4B5563] mt-1">VIN ή Μάρκα/Μοντέλο</div>
          </div>
          <ArrowRightIcon size={28} className="group-hover:translate-x-1 transition-transform" />
        </Link>
        <Link
          to="/inventory"
          data-testid="quick-inventory"
          className="border-b border-r border-black/10 p-6 hover:bg-[#F9FAFB] transition-colors group flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">Διαχείριση</div>
            <div className="heading text-xl font-bold mt-1">Στόλος</div>
            <div className="text-xs text-[#4B5563] mt-1">Επεξεργασία & κατάσταση</div>
          </div>
          <ArrowRightIcon size={28} className="group-hover:translate-x-1 transition-transform" />
        </Link>
        <Link
          to="/exports"
          data-testid="quick-exports"
          className="border-b border-r border-black/10 p-6 hover:bg-[#F9FAFB] transition-colors group flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">Έξοδος</div>
            <div className="heading text-xl font-bold mt-1">XML / JSON</div>
            <div className="text-xs text-[#4B5563] mt-1">car.gr · Google · WordPress</div>
          </div>
          <ArrowRightIcon size={28} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      {/* Fleet analytics */}
      {analytics && (
        <div data-testid="dashboard-analytics">
          <h2 className="heading text-xl font-extrabold tracking-tight mb-4">Αναλύσεις Στόλου</h2>

          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 border-t border-l border-black/10 mb-6">
            <KpiCell label="Μέση Τιμή" value={fmtEUR(analytics.avg_price)} testid="kpi-avg-price" />
            <KpiCell label="Μέσα Χλμ" value={new Intl.NumberFormat("el-GR").format(analytics.avg_mileage)} testid="kpi-avg-mileage" />
            <KpiCell label="Μέση Ηλικία" value={analytics.avg_age_years != null ? `${analytics.avg_age_years} έτη` : "—"} testid="kpi-avg-age" />
            <KpiCell label="Στο Site" value={analytics.on_site} sub="ενεργά στο WP" testid="kpi-on-site" />
            <KpiCell label="Πωλημένα" value={analytics.sold_count} sub={fmtEUR(analytics.sold_value)} testid="kpi-sold" />
            <KpiCell
              label="Νέες Αιτήσεις"
              value={analytics.new_sell_leads}
              sub="πούλησέ μας το αυτ/το"
              accent={analytics.new_sell_leads > 0 ? "#E60000" : undefined}
              testid="kpi-new-leads"
            />
          </div>

          {/* Breakdown panels */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <BreakdownPanel title="Ανά Μάρκα" items={analytics.by_brand} testid="breakdown-brand"
              subFmt={(it) => fmtEUR(it.value)} />
            <BreakdownPanel title="Ανά Χρονολογία" items={analytics.by_year} testid="breakdown-year"
              subFmt={(it) => fmtEUR(it.value)} />
            <BreakdownPanel title="Ανά Καύσιμο" items={analytics.by_fuel} testid="breakdown-fuel" />
            <BreakdownPanel title="Ανά Τύπο Αμαξώματος" items={analytics.by_body} testid="breakdown-body" />
            <BreakdownPanel title="Ανά Κιβώτιο" items={analytics.by_transmission} testid="breakdown-transmission" />
            <BreakdownPanel title="Εύρος Τιμών" items={analytics.price_ranges} testid="breakdown-price" />
            <BreakdownPanel title="Εύρος Χιλιομέτρων" items={analytics.mileage_ranges} testid="breakdown-mileage" />
            <BreakdownPanel title="Πωλήσεις ανά Μήνα" items={analytics.sales_by_month} testid="breakdown-sales"
              subFmt={(it) => fmtEUR(it.revenue)} />
            {analytics.no_photos > 0 && (
              <div className="border border-[#D97706] bg-[#FFFBEB] p-5 flex flex-col justify-center" data-testid="no-photos-warning">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#92400E]">Προσοχή</div>
                <div className="heading text-2xl font-extrabold text-[#92400E] mt-1">{analytics.no_photos} χωρίς φωτογραφίες</div>
                <div className="text-xs text-[#92400E] mt-1">αυτοκίνητα του στόλου χρειάζονται φωτογραφίες</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent listings */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="heading text-xl font-extrabold tracking-tight">Πρόσφατες Καταχωρήσεις</h2>
          <Link to="/inventory" className="text-sm underline underline-offset-4 hover:text-[#E60000]">
            Όλα τα οχήματα →
          </Link>
        </div>

        {recent.length === 0 ? (
          <div className="border border-black/10 p-16 text-center">
            <CarIcon size={48} className="mx-auto text-[#9CA3AF]" weight="thin" />
            <div className="heading text-xl font-bold mt-4">Άδειος στόλος</div>
            <div className="text-sm text-[#4B5563] mt-2">Καταχωρήστε το πρώτο σας αυτοκίνητο για να ξεκινήσετε.</div>
            <Link to="/cars/new" data-testid="empty-add-car" className="btn-primary inline-block mt-6">
              + Προσθήκη Αυτοκινήτου
            </Link>
          </div>
        ) : (
          <div className="border border-black/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F9FAFB] border-b-2 border-black/20">
                  {["Όχημα", "Έτος", "Χλμ.", "Καύσιμο", "Κατάσταση", "Τιμή"].map((h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.1em] text-[#4B5563] font-medium"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recent.map((c) => (
                  <tr key={c.id} className="border-b border-black/10 hover:bg-[#F9FAFB] transition-colors">
                    <td className="px-4 py-3">
                      <Link to={`/cars/${c.id}`} className="font-medium hover:text-[#E60000] flex items-center gap-2">
                        {c.featured && <StarIcon size={14} weight="fill" color="#F59E0B" />}
                        {c.brand} {c.model}
                        {c.variant ? <span className="text-[#4B5563] font-normal"> · {c.variant}</span> : null}
                      </Link>
                    </td>
                    <td className="px-4 py-3 font-mono">{c.year}</td>
                    <td className="px-4 py-3 font-mono">{new Intl.NumberFormat("el-GR").format(c.mileage_km)}</td>
                    <td className="px-4 py-3">{c.fuel_type}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-4 py-3 font-mono font-medium">{fmtEUR(c.price_eur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCell({ label, value, sub, accent, testid }) {
  return (
    <div className="border-b border-r border-black/10 bg-white p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#4B5563]">{label}</div>
      <div className="heading text-2xl font-extrabold tracking-tight mt-1" style={accent ? { color: accent } : undefined}>
        {value ?? "·"}
      </div>
      {sub && <div className="text-[11px] text-[#4B5563] mt-0.5 font-mono">{sub}</div>}
    </div>
  );
}

function BreakdownPanel({ title, items, subFmt, testid }) {
  const [expanded, setExpanded] = useState(false);
  if (!items || items.length === 0) return null;
  const max = Math.max(...items.map((i) => i.count), 1);
  const shown = expanded ? items : items.slice(0, 7);
  return (
    <div className="border border-black/10 bg-white p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563] mb-3">{title}</div>
      <div className="space-y-2">
        {shown.map((it) => (
          <div key={it.label} className="flex items-center gap-2 text-sm">
            <div className="w-28 truncate" title={it.label}>{it.label}</div>
            <div className="flex-1 h-4 bg-[#F3F4F6]">
              <div className="h-full bg-[#0A0A0A]" style={{ width: `${(it.count / max) * 100}%` }} />
            </div>
            <div className="w-8 text-right font-mono font-semibold">{it.count}</div>
            {subFmt && <div className="w-20 text-right text-[11px] text-[#4B5563] font-mono hidden xl:block">{subFmt(it)}</div>}
          </div>
        ))}
      </div>
      {items.length > 7 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 text-xs underline underline-offset-2 text-[#4B5563] hover:text-[#0A0A0A]"
          data-testid={`${testid}-toggle`}
        >
          {expanded ? "Λιγότερα" : `+ ${items.length - 7} ακόμα`}
        </button>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    available: { label: "Διαθέσιμο", bg: "bg-[#16A34A]" },
    reserved: { label: "Κρατημένο", bg: "bg-[#F59E0B]" },
    sold: { label: "Πωλήθηκε", bg: "bg-[#4B5563]" },
  };
  const s = map[status] || map.available;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider text-white ${s.bg}`}>
      <span className="w-1.5 h-1.5 bg-white" />
      {s.label}
    </span>
  );
}
